import { DueDiligenceFunnelTierSectionPdfLayout, SettingsPageGetDueDiligenceFunnelQuery } from '@/__generated__/graphql';
import CheckBox from '@/components/basicComponents/checkbox';
import Input from '@/components/basicComponents/input';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  CREATE_DUE_DILIGENCE_SECTION_MUTATION,
  GET_FUNNEL_QUERY,
  UPDATE_DRAFT_DUE_DILIGENCE_SECTION_MUTATION,
  UPDATE_DUE_DILIGENCE_SECTION_MUTATION
} from '../queries';

interface AddSectionProps {
  onClose: () => void;
  isOpen: boolean;
  currentSection: SettingsPageGetDueDiligenceFunnelQuery['getDueDiligenceFunnel']['tiers'][number]['sections'][number] | null;
  currentId: string;
  tierId: string | null;
  numberOfSections: number;
  isLimitedEdits: boolean;
}

const pdfLayoutValues = {
  ONE_COLUMN: 'One Column',
  TWO_COLUMN_GRID: 'Two Column Grid',
  THREE_COLUMN: 'Three Column'
};

const initialFormValues = {
  value: '',
  layout: 'Select',
  canScore: true
};

const AddSection = ({ onClose, isOpen, currentSection, currentId, tierId, numberOfSections, isLimitedEdits }: AddSectionProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [formValues, setFormValues] = useState(initialFormValues);

  const [createSection] = useMutation(CREATE_DUE_DILIGENCE_SECTION_MUTATION, {
    update(cache, { data }) {
      const currentData = cache.readQuery({
        query: GET_FUNNEL_QUERY,
        variables: {
          id: queryParams.get('funnelId')
        }
      });

      const { getDueDiligenceFunnel }: any = currentData;

      cache.writeQuery({
        query: GET_FUNNEL_QUERY,
        data: {
          getDueDiligenceFunnel: {
            ...getDueDiligenceFunnel,
            tiers: getDueDiligenceFunnel.tiers.map((tier: SettingsPageGetDueDiligenceFunnelQuery['getDueDiligenceFunnel']['tiers'][number]) =>
              tierId === tier.id
                ? {
                    ...tier,
                    sections: [
                      ...tier.sections,
                      {
                        ...data.createDueDiligenceFunnelTierSection,
                        order: numberOfSections ? numberOfSections + 1 : 1,
                        fields: []
                      }
                    ]
                  }
                : tier
            )
          }
        }
      });
    }
  });
  const [updateSection] = useMutation(UPDATE_DUE_DILIGENCE_SECTION_MUTATION);
  const [updateDraftSection] = useMutation(UPDATE_DRAFT_DUE_DILIGENCE_SECTION_MUTATION);

  useEffect(() => {
    if (currentSection) {
      setFormValues({
        value: currentSection.name,
        layout: pdfLayoutValues[currentSection.pdfLayout as keyof typeof pdfLayoutValues],
        canScore: currentSection.userCanScore
      });
    } else {
      setFormValues(initialFormValues);
    }
  }, [currentSection]);

  const getKeyByValue = (object: typeof pdfLayoutValues, value: string) => {
    return Object.keys(object).find((key) => object[key as keyof typeof object] === value);
  };

  const saveValue = () => {
    if (currentSection && isLimitedEdits) {
      updateSection({
        variables: {
          data: {
            id: currentId,
            name: formValues.value,
            pdfLayout: getKeyByValue(pdfLayoutValues, formValues.layout) as DueDiligenceFunnelTierSectionPdfLayout
          }
        },
        onCompleted: () => {
          onClose();
        }
      });

      return;
    }

    if (currentSection) {
      updateDraftSection({
        variables: {
          data: {
            id: currentId,
            name: formValues.value,
            userCanScore: formValues.canScore,
            pdfLayout: getKeyByValue(pdfLayoutValues, formValues.layout) as DueDiligenceFunnelTierSectionPdfLayout
          }
        }
      });
    } else {
      createSection({
        variables: {
          data: {
            funnelTierId: tierId,
            name: formValues.value,
            userCanScore: formValues.canScore,
            order: numberOfSections ? numberOfSections + 1 : 1,
            pdfLayout: getKeyByValue(pdfLayoutValues, formValues.layout) as DueDiligenceFunnelTierSectionPdfLayout
          }
        }
      });
    }
    onClose();
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title={currentSection ? 'Edit Section' : 'Add New Section'}
      confirmButton={{
        name: 'Save',
        onClick: saveValue,
        disabled: !formValues.value || formValues.layout === 'Select'
      }}
      showRequiredFields
    >
      <ModalContainer>
        <Input
          value={formValues.value}
          onChange={(event) => setFormValues({ ...formValues, value: event.target.value })}
          type="text"
          placeholder="Give this Section a unique name"
          label="Name"
          size="lg"
          autoFocus
          isRequiredField
        />
        <Select
          value={formValues.layout}
          setValue={(value) => setFormValues({ ...formValues, layout: value })}
          options={Object.values(pdfLayoutValues).map((item) => createSimpleSelectOption(item))}
          label="Layout"
          width="100%"
          size="lg"
          isRequiredField
        />
        <CheckboxWrapper
          id="input"
          onClick={
            isLimitedEdits
              ? () => null
              : () =>
                  setFormValues({
                    ...formValues,
                    canScore: !formValues.canScore
                  })
          }
          isDisabled={isLimitedEdits}
        >
          <CheckBox id="input" isActive={formValues.canScore} /> Allow User to Score This Section
        </CheckboxWrapper>
      </ModalContainer>
    </ModalWrappedContent>
  );
};

export default AddSection;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CheckboxWrapper = styled.div<{ isDisabled: boolean }>`
  cursor: ${({ isDisabled }) => (isDisabled ? 'auto' : 'pointer')};
  display: flex;
  gap: 9px;
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid ${themeVar('border.base')};
  color: ${themeVar('font.base')};
  font-size: 16px;
  font-weight: 400;
  opacity: ${({ isDisabled }) => (isDisabled ? '.5' : '1')};

  &:hover #input {
    cursor: ${({ isDisabled }) => (isDisabled ? 'auto' : 'pointer')};
  }
`;
